import { ComputedProperties } from '../../ReplicantComputedProperties';
import {
    GetIndexSchema,
    getIndexSchemaFromComputedProps,
    GetPayloadSchema,
    getPayloadSchemaFromComputedProps,
    QueryResults,
    SearchOpts,
} from '../../ReplicantIndexes';
import SB from '../../SchemaBuilder';
import { flattenComputedPropertySchema } from '../../utils/IndexingUtils';
import { FailedDocument, IndexDB } from '../IndexDB';
import {
    flattenDocument,
    generate,
    GeneratorStackFrame,
    preProcessQuery,
    QueryGenerator,
    tryValidateCount,
    tryValidateSearch,
    unFlattenDocument,
} from './IndexDBQueryUtils';

export default function createIndexDBDev<TComputedProperties extends ComputedProperties>(
    indexName: string,
    computedProps: TComputedProperties,
    db: {
        pushToIndex: (index: string, id: string, doc: unknown) => void;
        getFromIndex: (index: string, id: string) => any;
        getAllIds: (index: string) => string[];
        reset: () => void;
        deleteFromIndex: (index: string, id: string) => void;
    },
): IndexDB<TComputedProperties> {
    const rawSchema = getPayloadSchemaFromComputedProps(computedProps);
    const unflattenedPayloadSchema = getPayloadSchemaFromComputedProps(computedProps);
    const payloadFields = flattenComputedPropertySchema('', unflattenedPayloadSchema);
    const indexFields = flattenComputedPropertySchema('', getIndexSchemaFromComputedProps(computedProps));

    const validationSchema = SB.object({ ...unflattenedPayloadSchema });

    async function processQuery(query: SearchOpts<GetIndexSchema<TComputedProperties>>) {
        let results: QueryResults<GetPayloadSchema<TComputedProperties>>['results'] = [];

        // We keep dbValue and id here as they are implicitly captured
        // in the scope of the generator calls
        let dbValue: any;
        let id: string;

        const stack: GeneratorStackFrame[] = [{ fields: indexFields, path: '' }];
        const frame = () => stack[stack.length - 1];
        const value = (path?: string[]) => {
            let val = dbValue;
            if (path) {
                for (const segment of path) {
                    val = val[segment];
                }
            }

            return val;
        };

        function filter(fn: (val: any) => boolean) {
            const isNested = stack.length > 1;
            const path = isNested ? stack.slice(1).map((x) => x.path) : undefined;

            return () => {
                const val = value(path);
                if (isNested) {
                    return val.some((x: any) => fn(x));
                } else {
                    return fn(val);
                }
            };
        }

        const devGenerator: QueryGenerator<() => boolean> = {
            searchFields: () => frame()!.fields,

            finalize: (fragment) => fragment,
            pushStackFrame: (newFrame) => stack.push(newFrame),
            popStackFrame: () => stack.pop(),

            exists(field: string, value: boolean) {
                return filter((obj) => (value ? !!obj[field] : !obj[field]));
            },
            idOneOf(field: string, values: string[]) {
                return () => values.some((x) => x === id);
            },
            idNotOneOf(field: string, values: string[]) {
                return () => values.every((x) => x !== id);
            },
            between(field: string, bounds: [number, number]) {
                return filter((obj) => obj[field] >= bounds[0] && obj[field] <= bounds[1]);
            },
            lessThan(field: string, val: number) {
                return filter((obj) => obj[field] < val);
            },
            lessThanOrEqual(field: string, val: number) {
                return filter((obj) => obj[field] <= val);
            },
            greaterThan(field: string, val: number) {
                return filter((obj) => obj[field] > val);
            },
            greaterThanOrEqual(field: string, val: number) {
                return filter((obj) => obj[field] >= val);
            },
            containsAnyOf(field: string, values: any[]) {
                return filter((obj) => values.some((x) => (obj[field] as any[]).some((y) => y === x)));
            },
            containsNoneOf(field: string, values: any[]) {
                return filter((obj) => values.every((x) => (obj[field] as any[]).every((y) => y !== x)));
            },
            isAnyOf(field: string, values: any[]) {
                return filter((val) => values.some((x) => val[field] === x));
            },
            isNoneOf(field: string, values: any[]) {
                return filter((obj) => values.every((x) => obj[field] !== x));
            },

            matchesAnyOf(field: string, queryTerms: string[]) {
                return filter((obj) => {
                    const value = obj[field];
                    if (!value) {
                        return false;
                    }

                    if (typeof value !== 'string') {
                        throw Error(`Unexpected ${field} value ${value}: expecting a string`);
                    }

                    // Lightweight version of Opensearch's string matching algorithm:
                    const normalize = (input: string): string => input.trim().toLowerCase();

                    const normalizedValue = normalize(value);
                    const normalizedQueryTerms = queryTerms.flatMap((query) => query.split(' ')).map(normalize);

                    return normalizedQueryTerms.some((query) => normalizedValue.includes(query));
                });
            },

            is(field: string, val: boolean) {
                return filter((obj) => obj[field] === val);
            },

            isNot(field: string, val: boolean) {
                return filter((obj) => obj[field] !== val);
            },

            intersection(fragments: (() => boolean)[]) {
                return () => fragments.every((x) => x());
            },
            union(fragments: (() => boolean)[]) {
                return () => fragments.some((x) => x());
            },
        };

        const computeResult = generate(query, devGenerator);

        for (id of db.getAllIds(indexName)) {
            dbValue = db.getFromIndex(indexName, id);
            if (computeResult()) {
                const obj = { ...dbValue };
                for (const key in obj) {
                    if (!payloadFields[key as keyof typeof payloadFields]) {
                        delete obj[key];
                    }
                }
                results.push({ id, ...obj });
            }
        }

        const sort = query.sort;
        if (sort) {
            results = results.sort((a, b): -1 | 0 | 1 => {
                for (const sortCriteria of sort === 'random' ? [sort] : sort) {
                    if (sortCriteria === 'random') {
                        return Math.random() < 0.5 ? -1 : 1;
                    }

                    if ('script' in sortCriteria) {
                        continue; // Script-based sorting is not supported in development indexDB
                    }

                    if (sortCriteria.order !== 'asc' && sortCriteria.order !== 'desc') {
                        throw new Error('Unknown sort order: ' + (sortCriteria.order as any));
                    }

                    const field = sortCriteria.field as string;

                    const asc = sortCriteria.order === 'asc';
                    let valA;
                    let valB;

                    if (field.includes('.') && field.split('.').join('>') in indexFields) {
                        // Flattened object fields:
                        const flatObjectField = field.split('.').join('>');
                        valA = (a as any)[flatObjectField];
                        valB = (b as any)[flatObjectField];
                    } else if (field.includes('.')) {
                        // Nested fields:
                        const segments = field.split('.') as [string, ...string[]];

                        const valsA = (a as any)[segments[0]];
                        // find a value that qualifies;
                        for (const val of valsA) {
                            dbValue = { ...a, [segments[0]]: [val] };
                            if (computeResult()) {
                                // drill to get the value;
                                valA = segments.slice(1).reduce((f, path) => (f = f[path]), val);
                                break;
                            }
                        }
                        if (valA === undefined) {
                            throw new Error(
                                `Sorting on field ${field} failed as filtered results do not have this field`,
                            );
                        }

                        const valsB = (b as any)[segments[0]];
                        // find a value that qualifies;
                        for (const val of valsB) {
                            dbValue = { ...b, [segments[0]]: [val] };
                            if (computeResult()) {
                                // drill to get the value;
                                valB = segments.slice(1).reduce((f, path) => (f = f[path]), val);
                                break;
                            }
                        }
                        if (valB === undefined) {
                            throw new Error(
                                `Sorting on field ${field} failed as filtered results do not have this field`,
                            );
                        }
                    } else {
                        // Non-nested fields:
                        valA = (a as any)[field];
                        valB = (b as any)[field];
                    }

                    if (valA > valB) {
                        return asc ? 1 : -1;
                    } else if (valB > valA) {
                        return asc ? -1 : 1;
                    }
                }

                return 0;
            });
        }

        results = results.slice(0, query.limit).map((r) => unFlattenDocument(r)) as typeof results;

        return {
            results,
        };
    }

    const indexDB: IndexDB<TComputedProperties> = {
        batchPushDocuments: async (docs) => {
            for (const id in docs) {
                validationSchema.tryValidate(docs[id]);
                const flatDoc = flattenDocument(docs[id]!);
                db.pushToIndex(indexName, id, flatDoc);
            }

            return [] as FailedDocument[];
        },

        async getMany(ids) {
            const { results } = await this.query({ where: { id: { isOneOf: ids } } as any, limit: ids.length });

            return results.reduce((acc, res) => ({ ...acc, [res.id]: { indexedAt: 0, doc: res } }), {});
        },

        initialize: () => Promise.resolve(),

        query: async (query) => {
            query = preProcessQuery(rawSchema, query);
            tryValidateSearch(indexFields, query);

            if (query.limit === 0) {
                return { results: [] };
            }

            return processQuery(query);
        },

        count: async (query) => {
            query = preProcessQuery(rawSchema, query);
            tryValidateCount(indexFields, query);

            const { results } = await processQuery({ ...query, limit: 10_000 });

            return results.length;
        },

        scanIds: (opts) => {
            return async () => {
                const where =
                    opts.where && Object.keys(opts.where).length > 0 ? opts.where : { id: { isNotOneOf: [''] } }; // Bypass empty query validation

                const { results } = await indexDB.query({ where: where as any, limit: 10_000 });

                const clearScroll = async () => undefined; // no-op

                return { clearScroll, ids: results.map((r) => r.id) };
            };
        },

        recreateIndex: async () => {
            db.reset();
        },

        delete: async (userId: string) => {
            const exists = !!db.getFromIndex(indexName, userId);
            if (!exists) {
                return false;
            }

            db.deleteFromIndex(indexName, userId);
            return true;
        },

        nukeIndex: async () => {
            db.reset();
        },
    };

    return indexDB;
}
